import React from 'react';
import { Prompt, Redirect, useHistory, useParams } from 'react-router';
import { useCurrentuser } from '@uplift/contexts';
import { ProviderMedicalRole } from '@uplift/types';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useUserMatchesQuery } from '@uplift/queries';
import { useRematchingMachine } from './state-machine/machine';
import { UpliftThemeProvider } from '@uplift/ui-v2';
import { RematchingState } from './state-machine/machine.component';
import { LightLayout } from '../../components';
import { Loading } from '@uplift/pages';

type Params = {
  id: string;
};

export const RematchingPage: React.VFC = () => {
  const history = useHistory();
  const params = useParams<Params>();
  const me = useCurrentuser();
  const { data: matches, isLoading } = useUserMatchesQuery(me.id);
  const allMatches = matches?.allMatches ?? [];
  const latestMatchId = allMatches[0]?.match.id;

  if (isLoading) {
    return (
      <LightLayout onBack={history.goBack}>
        <Loading />
      </LightLayout>
    );
  }

  const matchId = params.id;

  if (!matchId) {
    const therapists = allMatches.filter((match) => match.provider.medicalRole === ProviderMedicalRole.therapist);

    switch (therapists.length) {
      case 0:
        return <Redirect push={false} to={ROUTING_CONFIG.matching} />;
      case 1:
        return <Redirect push={false} to={`/rematching/${allMatches[0].match.id}`} />;
      default:
        return <Redirect push={false} to={ROUTING_CONFIG.careTeam} />;
    }
  }

  if (matchId === 'new') {
    const therapistsCount = matches.activeMatches.filter((match) => match.provider.medicalRole === ProviderMedicalRole.therapist).length;

    if (therapistsCount > 0) {
      return <Redirect to="/" />;
    }

    return (
      <RematchingMachine
        latestMatchId={latestMatchId}
        matchId="new"
      />
    );
  }

  const match = allMatches.find((match) => match.match.id === matchId);

  if (!match || match.provider.medicalRole !== ProviderMedicalRole.therapist || match.match.state !== 'active') {
    return <Redirect to="/" />;
  }

  return (
    <RematchingMachine
      latestMatchId={latestMatchId}
      matchId={matchId}
      matchProviderId={match.provider.id}
    />
  );
};

type RematchingMachineProps = {
  latestMatchId: string;
  matchId: string;
  matchProviderId?: string;
};

const RematchingMachine: React.VFC<RematchingMachineProps> = props => {
  const machine = useRematchingMachine(props);
  const [ _, send ] = machine;

  return (
    <UpliftThemeProvider>
      <Prompt
        when={true}
        message={(_location, action) => {
          if (action === 'POP' && _.value !== 'WHY') {
            if (_.can({ type: 'BACK' })) {
              send({ type: 'BACK' });
              return false;
            }
            return 'You are going to leave rematching. Are you sure?';
          }
          return true;
        }}
      />
      <RematchingState
        stateMachine={machine}
        onBack={() => send({ type: 'BACK' })}
      />
    </UpliftThemeProvider>
  );
};
